import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, UtensilsCrossed, Wine, Coffee } from "lucide-react";
import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useBusiness } from "@/context/BusinessContext";
import { useT } from "@/lib/useT";
import { Link as RouterLink } from "react-router-dom";
import LoyaltyTeaseStrip from "@/components/LoyaltyTeaseStrip";
import GalleryCarousel from "@/components/GalleryCarousel";
import PushOptInPill from "@/components/PushOptInPill";

const HERO_IMG =
  "https://images.pexels.com/photos/32725237/pexels-photo-32725237.jpeg?w=2000&auto=compress";

const STORY_IMG =
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&auto=format&fit=crop&q=80";

const DEFAULT_PILLARS = [
  {
    icon: UtensilsCrossed,
    title: "Italian Soul",
    body: "Hand-rolled pasta, wood-fired pizza, and recipes passed through generations.",
  },
  {
    icon: Wine,
    title: "Australian Wines",
    body: "A considered cellar from Barossa, Margaret River, Coonawarra and the Yarra Valley.",
  },
  {
    icon: Coffee,
    title: "Slow Hospitality",
    body: "Every espresso, every plate — served with the care of a Sunday lunch at nonna's.",
  },
];

const DEFAULT_STORY = {
  eyebrow: "Our Story",
  heading: "A neighbourhood favourite, run with heart.",
  body:
    "We opened with a single belief — that great food and warm service belong together, every day of the week. Years on, we still handle every detail ourselves and welcome every guest like an old friend.",
  cta: "See the menu",
};

// Per-tenant overrides for the Pillars + Story bento.
// Keyed by `business.slug` so seeded tenants render their own voice
// instead of inheriting the default copy.
const TENANT_OVERRIDES = {
  "coffee-commune": {
    pillars: [
      { icon: Coffee, title: "Voted QLD's Best Coffee 2025", body: "Beans roasted on-site by Coffee Alliance, pulled by champion baristas. Single-origin to signature blends, retail to wholesale." },
      { icon: UtensilsCrossed, title: "Italian brunch, Bowen Hills", body: "L'Angolo Italiano — Frittata, Bolognese di Nonna, La Bistecca. Chef-led seasonal menu, served Mon–Sat." },
      { icon: Wine, title: "A coffee community hub", body: "Roastery, training school, event spaces, membership network. The town square for the Aussie coffee industry." },
    ],
    story: {
      eyebrow: "About The Coffee Commune",
      heading: "From selling coffee, to growing people.",
      body: "Founded by Phillip Di Bella — the name synonymous with Australian coffee — The Coffee Commune is a purpose-built ecosystem where independent brands, cafe owners and hospitality pros share the infrastructure usually reserved for global franchises. Our roastery contracts roast for you. Our green-bean arm sources transparent, traceable beans. Our cafe is a living laboratory. Our training school sharpens you. Our community has your back.",
      cta: "Explore the cafe",
    },
  },
  "oxley-cafe": {
    pillars: [
      { icon: Coffee, title: "Single-origin coffee", body: "Roasted in Brisbane, pulled fresh on a La Marzocco. Brew guides on every bag." },
      { icon: UtensilsCrossed, title: "All-day brunch", body: "Smashed avo, eggs your way, and a vegan pancake stack that doesn't apologise." },
      { icon: Wine, title: "Neighbourhood local", body: "Walk-ins welcome. We know our regulars by their order, not their names." },
    ],
    story: {
      eyebrow: "Our Story",
      heading: "Twelve years on Cliveden Avenue. We're still here.",
      body: "Started by Hannah and Pete as a 3-table corner café in 2014, Oxley Cafe is now a daily ritual for the neighbourhood. Same recipes. Same beans. Slightly bigger kitchen.",
      cta: "See the menu",
    },
  },
  "kebab-zone-oxley": {
    pillars: [
      { icon: UtensilsCrossed, title: "Charcoal-grilled", body: "Real charcoal, not a flame grill. The reason your kebab tastes different here." },
      { icon: Coffee, title: "Late-night ready", body: "Open till 10:30pm Fri-Sat. The post-game, post-shift, post-everything snack pack stop." },
      { icon: Wine, title: "Family-run, Oxley raised", body: "The Bennett family has fed Oxley since 2019. Three generations in the kitchen." },
    ],
    story: {
      eyebrow: "Our Story",
      heading: "From a single charcoal grill to the loudest kitchen in Oxley Plaza.",
      body: "We opened Kebab Zone with one charcoal grill, six skewers and a stubborn belief that fast food can still mean real food. Five years in, the grill never stops and the queue never shortens.",
      cta: "See the menu",
    },
  },
};

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const settings = useSettings();
  const { business } = useBusiness();
  const t = useT();

  const heroImg = business?.hero_image_url || settings?.hero_image_url || HERO_IMG;
  const heroHeadline = t(business, "hero_headline") || "";
  const heroSub = t(business, "hero_subheadline") ||
    "A modern trattoria pouring the best of Australian winemaking alongside heritage Italian cooking. Reserve a table or pre-order to take home.";
  const ctaLabel = t(business, "cta_label") || "Explore the Menu";
  const ctaLink  = business?.cta_link  || "/menu";
  // Eyebrow — prefer the business's own suburb/postcode + established year
  // when present, fall back to the legacy Carlton default.
  // Per-tenant overrides — declared first because the eyebrow helper below
  // references `slug` to pick a sensible fallback when there's no tagline.
  const slug = business?.slug;
  const tenant = (slug && TENANT_OVERRIDES[slug]) || {};
  const PILLARS = tenant.pillars || DEFAULT_PILLARS;
  const STORY   = tenant.story   || DEFAULT_STORY;
  // Per-tenant story image — uses the hero if no dedicated photo is set.
  const storyImg = business?.story_image_url || business?.hero_image_url || STORY_IMG;

  const heroEyebrow = (() => {
    const suburb = business?.suburb;
    const est    = business?.established_year || business?.est_year;
    const tag    = business?.tagline;
    if (suburb && est) return `${suburb} · Est. ${est}`;
    if (suburb && tag) return `${suburb} · ${tag.split("·").slice(-1)[0].trim()}`;
    if (tag) return tag;
    if (suburb) return suburb;
    if (slug) return business?.business_name || "";
    return "Carlton · Est. 2008";
  })();
  // Reservations gate — tenants that don't take bookings (e.g. quick-service
  // kebab shops) explicitly turn this off via features.table_bookings.
  const bookingsEnabled = business?.features?.table_bookings !== false;

  function renderHeadline(text) {
    if (!text) {
      return (
        <>
          Italy on the plate.
          <br />
          <span className="italic text-[#E8B4A2]">Australia in the glass.</span>
        </>
      );
    }
    // Split *italic phrase* tokens. Renders italic spans for paired asterisks.
    const parts = text.split(/(\*[^*]+\*)/g).filter(Boolean);
    return parts.map((p, i) =>
      p.startsWith("*") && p.endsWith("*") ? (
        <span key={i} className="italic text-[#E8B4A2] block">
          {p.slice(1, -1)}
        </span>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/menu", { params: { tag: "chef_special" } });
        setFeatured(data.slice(0, 4));
      } catch (_e) {
        setFeatured([]);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[88vh] min-h-[560px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="Restaurant interior"
            className="w-full h-full object-cover ken-burns"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/70"></div>
        </div>

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 md:pb-24">
          <p
            data-testid="hero-eyebrow"
            className="text-xs uppercase tracking-[0.25em] font-bold text-[#FDFBF7]/80 fade-up"
          >
            {heroEyebrow}
          </p>
          <h1
            data-testid="hero-title"
            className="font-serif-display text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight max-w-3xl mt-4 fade-up-delay-1"
          >
            {renderHeadline(heroHeadline)}
          </h1>
          <p
            data-testid="hero-subtitle"
            className="mt-6 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed fade-up-delay-2"
          >
            {heroSub}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 fade-up-delay-3">
            {/* When the tenant has its own primary CTA (e.g. café "Pre-order coffee"),
                that becomes the orange button and "Reserve a Table" steps back into
                the outline slot. Restaurants without a custom CTA keep the original
                Reserve-first hierarchy. */}
            {business?.cta_link && business?.cta_link !== "/book" ? (
              <>
                <Link to={ctaLink || "/menu"}>
                  <Button
                    data-testid="hero-cta-button"
                    className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-8 py-6 text-base"
                  >
                    {ctaLabel}
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </Link>
                {bookingsEnabled && (
                  <Link to="/book">
                    <Button
                      data-testid="hero-book-button"
                      variant="outline"
                      className="border-white text-white bg-transparent hover:bg-white hover:text-[#121614] rounded-full px-8 py-6 text-base"
                    >
                      Reserve a Table
                    </Button>
                  </Link>
                )}
              </>
            ) : (
              <>
                {bookingsEnabled && (
                  <Link to="/book">
                    <Button
                      data-testid="hero-book-button"
                      className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-8 py-6 text-base"
                    >
                      Reserve a Table
                    </Button>
                  </Link>
                )}
                <Link to={ctaLink || "/menu"}>
                  <Button
                    data-testid="hero-menu-button"
                    variant={bookingsEnabled ? "outline" : "default"}
                    className={bookingsEnabled
                      ? "border-white text-white bg-transparent hover:bg-white hover:text-[#121614] rounded-full px-8 py-6 text-base"
                      : "bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-8 py-6 text-base"}
                  >
                    {ctaLabel}
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex flex-col gap-4"
              >
                <div className="h-14 w-14 rounded-2xl bg-[#F3F0EA] flex items-center justify-center text-[#C05746]">
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif-display text-2xl">{p.title}</h3>
                <p className="text-[#5C625E] leading-relaxed text-sm">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Phase 25 — Loyalty join CTA. Self-hides when program is inactive. */}
      <LoyaltyTeaseStrip />

      {/* Phase 25 — Gallery carousel. Self-hides if no carousel photos. */}
      <GalleryCarousel />

      {/* Story bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <div className="lg:col-span-7 rounded-2xl overflow-hidden border border-[#E8E5DF] bg-white">
            <img
              src={storyImg}
              alt={STORY.heading}
              className="w-full h-72 md:h-[440px] object-cover"
            />
          </div>
          <div className="lg:col-span-5 bg-[#F3F0EA] rounded-2xl p-8 md:p-12 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
              {STORY.eyebrow}
            </p>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight">
              {STORY.heading}
            </h2>
            <p className="mt-5 text-[#5C625E] leading-relaxed">
              {STORY.body}
            </p>
            <Link to="/menu" className="mt-6 inline-flex">
              <Button
                data-testid="story-cta-button"
                className="bg-[#121614] hover:bg-black text-white rounded-full px-6"
              >
                {STORY.cta}
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured chef specials */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-32">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
                Chef Specials
              </p>
              <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-2">
                Tonight, we're pouring & plating…
              </h2>
            </div>
            <RouterLink
              to="/menu"
              data-testid="featured-see-all"
              className="text-sm text-[#C05746] hover:text-[#A64A3C] inline-flex items-center gap-1"
            >
              See full menu <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </RouterLink>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {featured.map((m) => (
              <RouterLink
                key={m.id}
                to="/menu"
                data-testid={`featured-item-${m.id}`}
                className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col"
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src={m.image_url}
                    alt={m.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-serif-display text-xl">{m.name}</h3>
                  <p className="text-sm text-[#5C625E] mt-2 line-clamp-2">{m.description}</p>
                  <p className="mt-3 font-medium text-[#C05746]">{money(m.price)}</p>
                </div>
              </RouterLink>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
